// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import type {ScrollingCalendar} from './index';
import type {SelectionLayout} from './types';

const SelectionSegment = ({
  selectionLayout,
  selectionClass,
  component,
}: {
  selectionLayout: SelectionLayout;
  selectionClass: string;
  component: ScrollingCalendar;
}) => {
  return (
    <>
      {selectionLayout.segments.map((segment) => (
        <div
          sel='.selection-segment'
          class={{
            'start-rounded': segment.startRounded,
            'end-rounded': segment.endRounded,
            [selectionClass || undefined]: !!selectionClass,
          }}
          style={{
            background: component.helpers.getSelectionSegmentBackground(segment),
            'grid-row': segment.rowStart.toString(),
            'grid-column': `${segment.columnStart}` + ` / ` + `span ${segment.columnSpan}`,
            margin: `${segment.verticalMarginPx}px 0`,
          }}
        />
      ))}
    </>
  );
};

export function template(this: ScrollingCalendar) {
  const {print} = this.getContext(`i18n`);

  const weekdayLabels = this.helpers.getOrderedWeekdayLabels();
  const monthGridLayout = this.helpers.getVisibleMonthGridLayout();

  /* canary elements are invisible, they exist to be watched by IntersectionObserver; */
  /* when a canary element scrolls into view, it's a sign that more calendar months need to be loaded */
  const selectionLayout = this.helpers.getSelectionLayout();
  const previewSelectionLayout = this.helpers.getPreviewSelectionLayout();
  /* ensure that days are rendered last so they are naturally at the top of DOM stack (relevant for appearance and .month-grid click delegation) */
  const todayKey = this.helpers.getTodayKey();

  return (
    <div
      sel='.scroll-container'
      attrs={{
        elref: `scrollContainer`,
      }}
      hook={{
        postpatch: this.helpers.handleScrollContainerPostpatch,
      }}
      on={{
        mousedown: this.helpers.handleScrollContainerMousedown,
      }}
    >
      {/* having a .content-wrapper also has the additional purpose of working around a weird Safari bug with position-sticky (breaks when more than one child div) */}
      <div sel='.content-wrapper'>
        <>
          <div sel='.header-wrapper'>
            <div
              sel='.week-header'
              style={{
                height: `${this.helpers.HEADER_HEIGHT_PX}px`,
                'grid-template-columns': `repeat(7, ${this.helpers.ROW_HEIGHT_PX}px)`,
              }}
            >
              {weekdayLabels.map((label) => (
                <div sel='.weekday-heading'>{label}</div>
              ))}
            </div>
          </div>
          <div sel='.grid-wrapper'>
            <div
              sel='.month-grid'
              style={{
                'grid-template-rows': `repeat(${monthGridLayout.rowHeight}, ${this.helpers.ROW_HEIGHT_PX}px)`,
                'grid-template-columns': `repeat(7, ${this.helpers.ROW_HEIGHT_PX}px)`,
              }}
              on={{
                /* delegate click handling to avoid adding/removing many event listeners and mass lambda creation */
                click: this.helpers.handleDelegatedClickHandler,
              }}
              hook={{
                /* snabbdom does not support event handlers that use capture, so we do it manually in hooks */
                insert: (vnode) => {
                  vnode.elm.addEventListener(`mouseenter`, this.helpers.handleDelegatedMouseEnterHandler, true);
                  vnode.elm.addEventListener(`mouseleave`, this.helpers.handleDelegatedMouseLeaveHandler, true);
                },
                destroy: (vnode) => {
                  vnode.elm.removeEventListener(`mouseenter`, this.helpers.handleDelegatedMouseEnterHandler, true);
                  vnode.elm.removeEventListener(`mouseleave`, this.helpers.handleDelegatedMouseLeaveHandler, true);
                },
              }}
            >
              <>
                <div
                  sel='.canary'
                  key='top-canary'
                  style={{
                    'grid-row': `1 / span ${monthGridLayout.topCanaryRowStart}`,
                  }}
                  hook={{insert: this.helpers.handleTopCanaryInsert}}
                />
                <div
                  sel='.canary'
                  key='bottom-canary'
                  style={{
                    'grid-row':
                      `${monthGridLayout.bottomCanaryRowStart}` +
                      ` / ` +
                      `span ${monthGridLayout.rowHeight - monthGridLayout.bottomCanaryRowStart}`,
                  }}
                  hook={{insert: this.helpers.handleBottomCanaryInsert}}
                />
                {selectionLayout ? (
                  <SelectionSegment selectionLayout={selectionLayout} selectionClass={null} component={this} />
                ) : null}
                {previewSelectionLayout ? (
                  <SelectionSegment
                    selectionLayout={previewSelectionLayout}
                    selectionClass={`preview`}
                    component={this}
                  />
                ) : null}
                {Array.from(monthGridLayout.months.values()).map((month) => (
                  <div
                    sel='.month-label'
                    style={{
                      'grid-row': `${month.rowStart}`,
                    }}
                  >
                    {print(month.label)}
                  </div>
                ))}
                {Array.from(monthGridLayout.days.keys()).map((dayKey) => {
                  const day = monthGridLayout.days.get(dayKey);
                  /* this container div exists to tessellate neatly because the circular decorations leave a lot of gaps */

                  const selectionSegment = this.helpers.getSelectionSegmentForDayKey(dayKey, selectionLayout);
                  const previewSelectionSegment = this.helpers.getSelectionSegmentForDayKey(
                    dayKey,
                    previewSelectionLayout,
                  );
                  const segmentType =
                    (previewSelectionSegment || selectionSegment) &&
                    (previewSelectionSegment || selectionSegment).segmentType;
                  const isToday = todayKey === dayKey;
                  // 0 based
                  const month = day.calendarDay.month < 9 ? `0${day.calendarDay.month + 1}` : day.calendarDay.month + 1;

                  return (
                    <div
                      sel='.day-of-month-square-container'
                      key={
                        dayKey /* not strictly necessary but it gets rid of visual flashes when snabbdom tries to reuse elements */
                      }
                      attrs={{
                        elref: `cell_${day.columnStart}_${day.rowStart}`,
                        role: `button`,
                        'aria-label': `${day.calendarDay.year}-${month}-${day.calendarDay.isoDayOfMonth}`,
                      }}
                      class={{disabled: day.disabled}}
                      style={{
                        'grid-column': `${day.columnStart}`,
                        'grid-row': `${day.rowStart}`,
                      }}
                      props={{
                        calendarDay: day.calendarDay /* used in .month-grid delegated event handlers */,
                      }}
                    >
                      <>
                        <div
                          sel='.day-of-month'
                          attrs={
                            isToday
                              ? {'data-text': day.calendarDay.isoDayOfMonth}
                              : {} /* data-text used in CSS ::after content */
                          }
                          class={{
                            today: isToday,
                            'preview-segment': !!previewSelectionSegment,
                            'segment-highlight': segmentType === this.helpers.SelectionSegmentType.Highlight,
                            'segment-offset': segmentType === this.helpers.SelectionSegmentType.Offset,
                            'segment-fill': segmentType === this.helpers.SelectionSegmentType.Fill,
                          }}
                        >
                          {day.calendarDay.isoDayOfMonth}
                        </div>
                        {this.helpers.renderDayTooltip(day.calendarDay)}
                      </>
                    </div>
                  );
                })}
              </>
            </div>
          </div>
        </>
      </div>
    </div>
  );
}
